import type Dinero from "dinero.js";
import React, { type JSX, useState } from "react";
import { addProductToBasket } from "tsi-common-react/src/apps/configurator/actions";
import { Image } from "tsi-common-react/src/common/Image";
import {
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import { IImageURL, isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero } from "tsi-common-react/src/utils/money";
import { navigateToURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { TinyProduct } from "./TinyProduct";

interface IProps {
    product: IProduct;
    variant: IProduct;
    bundle: IConcreteBundle;
    bundleProductIndex?: number;
}

/**
 * Calculate the combined price of two products in a bundle.
 */
const calculateCombinedPrice = (
    triggeringVariant: IProduct,
    suggestedProduct: IProduct,
): Dinero.Dinero | null => {
    if (
        triggeringVariant.price.cosmetic_excl_tax &&
        suggestedProduct.price.cosmetic_excl_tax
    ) {
        return getDinero(triggeringVariant.price.cosmetic_excl_tax).add(
            getDinero(suggestedProduct.price.cosmetic_excl_tax),
        );
    }
    return null;
};

/**
 * Determine the best image to display for the bundle.
 */
const getBundleImageSrc = (
    bundle: IConcreteBundle,
    suggestedProduct: IProduct,
    rootSuggestedProduct: IProduct,
): IImageURL | string | undefined => {
    const bundleGroup = bundle.bundle_group;

    if (bundleGroup.image) {
        return bundleGroup.image;
    }
    if (suggestedProduct.images.length > 0) {
        return suggestedProduct.images[0].original;
    }
    if (rootSuggestedProduct.images.length > 0) {
        return rootSuggestedProduct.images[0].original;
    }
    return undefined;
};

/**
 * Add both bundle products to the basket and track analytics events.
 */
const handleAddBundleToBasket = async (
    variant: IProduct,
    suggestedProduct: IProduct,
    onComplete: () => void,
): Promise<void> => {
    const p1 = addProductToBasket(variant.url, 1);
    const p2 = addProductToBasket(suggestedProduct.url, 1);

    await Promise.all([p1, p2]);

    // TODO(i18n): Wrap user-facing string in t tagged template: t`Added`
    onComplete();
    trackAddToBasketEvent(variant, 1);
    trackAddToBasketEvent(suggestedProduct, 1);
    navigateToURL(isoWebPageURL.wrap(reverse("basket:summary")));
};

/**
 * A two-product bundle shown on the PDP.
 *
 * Displays both products together, different from the InContextBundle, which
 * only displays one product, relying on page-context to imply the other product.
 */
export const OutOfContextBundle = (props: IProps): React.ReactNode => {
    const bundleProductIndex = props.bundleProductIndex ?? 0;

    // TODO(i18n): Wrap user-facing string in t tagged template: t`Buy Together`
    const [buyIsDisabled, setBuyIsDisabled] = useState(false);
    const [addToBasketButtonText, setAddToBasketButtonText] =
        useState("Buy Together");

    const suggestedProduct =
        props.bundle.suggested_products[bundleProductIndex];
    const rootSuggestedProduct = suggestedProduct.parent || suggestedProduct;
    const triggeringProduct = props.product;
    const triggeringVariant = props.variant;
    const combinedPrice = calculateCombinedPrice(
        triggeringVariant,
        suggestedProduct,
    );

    // TODO: Fix math error for calculating financing.
    const formattedCombinedPrice = format.money(combinedPrice);
    const priceElem: JSX.Element = (
        <div className="pdp-bundle-info__price">
            {t`Together ${formattedCombinedPrice}`}
        </div>
    );

    const imageSrc = getBundleImageSrc(
        props.bundle,
        suggestedProduct,
        rootSuggestedProduct,
    );

    const addToBasket = (): void => {
        setBuyIsDisabled(true);
        void handleAddBundleToBasket(props.variant, suggestedProduct, () =>
            setAddToBasketButtonText("Added"),
        );
    };

    return (
        <div className="pdp-bundle">
            <div className="pdp-bundle-images">
                <div className="pdp-bundle__image--hero">
                    <Image
                        className="responsive-img pdp-bundle-img"
                        src={imageSrc}
                    />
                </div>
                <div className="pdp-bundle__group">
                    <TinyProduct
                        product={triggeringProduct}
                        variant={triggeringVariant}
                        last={false}
                    />
                    <TinyProduct
                        product={rootSuggestedProduct}
                        variant={suggestedProduct}
                        last={true}
                    />
                </div>
            </div>
            <div className="pdp-bundle-info">
                <div className="pdp-bundle-info__copy">
                    <div className="pdp-bundle-info__title">
                        {t`${triggeringProduct.title} and ${rootSuggestedProduct.title}`}
                    </div>
                    <div className="pdp-bundle-info__desc">
                        {props.bundle.bundle_group.description}
                    </div>
                    {priceElem}
                </div>
                <div className="pdp-bundle-info__cta">
                    <button
                        className="button pdp-bundle-info__button al-pdp-bundle__info-button"
                        disabled={buyIsDisabled}
                        onClick={addToBasket}
                    >
                        {addToBasketButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
};
